// ---------------------------------------------------------------------------
// YouAreYou (uru) — Typed API Endpoint Functions
//
// Every function in this module maps 1:1 to an endpoint in docs/api.md.
// All calls go through the centralized apiClient — no raw fetch() usage.
//
// Functions are grouped by domain and ordered to match the API doc sections:
//   1. Phone Normalization
//   2. Registration
//   3. Authentication — Magic Link
//   4. Authentication — Passkeys (WebAuthn)
//   5. Passkey Registration (account management)
//   6. Passkey Management
//   7. Authentication — SMS OTP
//   8. Token Refresh
//   9. Current User
//  10. Connections — Resolve & CRUD
//  11. Connection Actions
//  12. KYC Status Check
// ---------------------------------------------------------------------------

import { apiClient } from "@/api/client";
import { resolveAvatarUrl } from "@/types/api";
import type {
  // File upload
  FileUploadInitiateData,
  // Phone
  NormalizePhoneRequest,
  // Notifications
  AppNotification,
  NotificationsQueryParams,
  NormalizePhoneData,
  // Registration
  RegisterStartRequest,
  RegisterStartResponse,
  RegisterVerifyRequest,
  RegisterVerifyResponse,
  RegisterCompleteRequest,
  RegisterCompleteResponse,
  // Connection phone verification
  ConnectStartRequest,
  ConnectVerifyRequest,
  ConnectVerifyResponse,
  // Auth — Magic Link
  MagicLoginRequest,
  AuthLoginData,
  // Auth — Passkeys
  PasskeyLoginBeginRequest,
  PasskeyLoginBeginData,
  PasskeyLoginCompleteRequest,
  // Passkey Registration (after first login)
  PasskeyRegisterBeginData,
  PasskeyRegisterCompleteRequest,
  // Passkey Management
  PasskeyInfo,
  // Auth — SMS OTP
  SmsLoginRequest,
  SmsVerifyRequest,
  // Token Refresh
  RefreshTokenRequest,
  RefreshTokenData,
  // User
  UserProfile,
  UruMeData,
  // Connections
  ConnectionsQueryParams,
  Connection,
  CreateDirectLinkConfig,
  ResolveConnectionData,
  ConnectionAction,
  SharableField,
  SharingMode,
  // KYC
  KycStatusData,
  SelfKycCreateRequest,
  SelfKycCreateResponse,
  SelfKycStatusResponse,
  GlobalLinkConfigRequest,
  // DocSign — public recipient flow
  DocSignDocumentData,
  DocSignStatusData,
  ExistingSignaturesData,
  SubmitSignatureRequest,
  SubmitSignatureData,
  // DocSign — admin read models (Documents page)
  DocSignRequestSummary,
  DocumentTemplateSummary,
  AccountGroup,
} from "@/types/api";

// ---------------------------------------------------------------------------
// 1. Phone Normalization
// ---------------------------------------------------------------------------

/**
 * POST /api/phonehub/number/normalize
 *
 * Normalizes a phone number to E.164 format.
 * Public — no auth required.
 */
export function normalizePhone(
  data: NormalizePhoneRequest,
): Promise<NormalizePhoneData> {
  return apiClient.post<NormalizePhoneData>(
    "/api/phonehub/number/normalize",
    data,
    { noAuth: true },
  );
}

// ---------------------------------------------------------------------------
// 2. Registration — 3-step phone-verified flow
// ---------------------------------------------------------------------------

/**
 * POST /api/uru/register/start
 *
 * Step 1 of registration. Sends an SMS verification link to the phone number.
 * Public — no auth required.
 *
 * NOTE: Always returns 200 (enumeration prevention). The UI must never reveal
 * whether the phone already exists.
 */
export function registerStart(
  data: RegisterStartRequest,
): Promise<RegisterStartResponse> {
  return apiClient.post<RegisterStartResponse>(
    "/api/uru/register/start",
    data,
    { noAuth: true },
  );
}

/**
 * POST /api/uru/register/verify
 *
 * Step 2 of registration. Verifies phone ownership via the token from SMS.
 * Returns next_step to determine which form to show.
 * Public — no auth required.
 */
export function registerVerify(
  data: RegisterVerifyRequest,
): Promise<RegisterVerifyResponse> {
  return apiClient.post<RegisterVerifyResponse>(
    "/api/uru/register/verify",
    data,
    { noAuth: true },
  );
}

/**
 * POST /api/uru/register/complete
 *
 * Step 3 of registration. Creates identity and starts KYC (new person) or
 * provisions uru account immediately (already KYC'd person — fast path).
 * Public — no auth required.
 */
export function registerComplete(
  data: RegisterCompleteRequest,
): Promise<RegisterCompleteResponse> {
  return apiClient.post<RegisterCompleteResponse>(
    "/api/uru/register/complete",
    data,
    { noAuth: true },
  );
}

// ---------------------------------------------------------------------------
// 2b. Connection Recipient Phone Verification
// ---------------------------------------------------------------------------

/**
 * POST /api/uru/connect/start
 *
 * Sends an SMS verification link to the recipient's phone before they can
 * accept/deny a connection. Associates the verification with the connection.
 * Public — no auth required.
 */
export function connectStart(
  data: ConnectStartRequest,
): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>(
    "/api/uru/connect/start",
    data,
    { noAuth: true },
  );
}

/**
 * POST /api/uru/connect/verify
 *
 * Verifies the recipient's phone via the token from SMS.
 * Returns next_step: already_verified | needs_kyc | needs_registration.
 * Public — no auth required.
 */
export function connectVerify(
  data: ConnectVerifyRequest,
): Promise<ConnectVerifyResponse> {
  return apiClient.post<ConnectVerifyResponse>(
    "/api/uru/connect/verify",
    data,
    { noAuth: true },
  );
}

// ---------------------------------------------------------------------------
// 3. Authentication — Magic Link
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/magic/login
 *
 * @deprecated Magic links are no longer sent by the uru backend.
 * First login now uses SMS OTP. Kept for backward compatibility with
 * any existing magic link tokens that may still be in transit.
 *
 * Exchanges a magic link token (ml:...) for a JWT.
 * Public — no auth required.
 */
export function magicLogin(data: MagicLoginRequest): Promise<AuthLoginData> {
  return apiClient.post<AuthLoginData>("/api/auth/magic/login", data, {
    noAuth: true,
  });
}

// ---------------------------------------------------------------------------
// 4. Authentication — Passkeys (WebAuthn) Login
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/passkeys/login/begin
 *
 * Initiates passkey login. Returns WebAuthn challenge options for
 * navigator.credentials.get().
 * Public — no auth required.
 */
export function passkeyLoginBegin(
  data: PasskeyLoginBeginRequest,
): Promise<PasskeyLoginBeginData> {
  return apiClient.post<PasskeyLoginBeginData>(
    "/api/auth/passkeys/login/begin",
    data,
    { noAuth: true },
  );
}

/**
 * POST /api/auth/passkeys/login/complete
 *
 * Completes passkey login. Submits the WebAuthn credential response.
 * Returns JWT (same shape as magic login).
 * Public — no auth required.
 */
export function passkeyLoginComplete(
  data: PasskeyLoginCompleteRequest,
): Promise<AuthLoginData> {
  return apiClient.post<AuthLoginData>(
    "/api/auth/passkeys/login/complete",
    data,
    { noAuth: true },
  );
}

// ---------------------------------------------------------------------------
// 5. Passkey Registration (after first login, requires JWT)
// ---------------------------------------------------------------------------

/**
 * POST /api/account/passkeys/register/begin
 *
 * Initiates passkey registration. Returns WebAuthn creation options for
 * navigator.credentials.create().
 * Requires JWT + Origin header.
 */
export function passkeyRegisterBegin(): Promise<PasskeyRegisterBeginData> {
  return apiClient.post<PasskeyRegisterBeginData>(
    "/api/account/passkeys/register/begin",
  );
}

/**
 * POST /api/account/passkeys/register/complete
 *
 * Completes passkey registration with the credential from
 * navigator.credentials.create().
 * Requires JWT + Origin header.
 */
export function passkeyRegisterComplete(
  data: PasskeyRegisterCompleteRequest,
): Promise<PasskeyInfo> {
  return apiClient.post<PasskeyInfo>(
    "/api/account/passkeys/register/complete",
    data,
  );
}

// ---------------------------------------------------------------------------
// 6. Passkey Management
// ---------------------------------------------------------------------------

/**
 * GET /api/account/passkeys
 *
 * Lists all passkeys registered by the authenticated user.
 * Requires JWT.
 */
export function listPasskeys(): Promise<PasskeyInfo[]> {
  return apiClient.get<PasskeyInfo[]>("/api/account/passkeys");
}

/**
 * POST /api/account/passkeys/{id}
 *
 * Updates a passkey (rename or disable).
 * Requires JWT.
 */
export function updatePasskey(
  id: number,
  data: Partial<Pick<PasskeyInfo, "friendly_name">>,
): Promise<PasskeyInfo> {
  return apiClient.post<PasskeyInfo>(`/api/account/passkeys/${id}`, data);
}

/**
 * DELETE /api/account/passkeys/{id}
 *
 * Removes a passkey.
 * Requires JWT.
 */
export function deletePasskey(id: number): Promise<void> {
  return apiClient.del<void>(`/api/account/passkeys/${id}`);
}

// ---------------------------------------------------------------------------
// 7. Authentication — SMS OTP
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/sms/login
 *
 * Sends a 6-digit OTP code via SMS.
 * Public — no auth required.
 *
 * NOTE: Always returns success regardless of whether user exists
 * (enumeration prevention).
 */
export function smsLogin(
  data: SmsLoginRequest,
): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>("/api/auth/sms/login", data, {
    noAuth: true,
  });
}

/**
 * POST /api/auth/sms/verify
 *
 * Verifies the SMS OTP code. Returns JWT (same shape as magic login).
 * Public — no auth required.
 *
 * Code expires after 10 minutes.
 */
export function smsVerify(data: SmsVerifyRequest): Promise<AuthLoginData> {
  return apiClient.post<AuthLoginData>("/api/auth/sms/verify", data, {
    noAuth: true,
  });
}

// ---------------------------------------------------------------------------
// 8. Token Refresh
// ---------------------------------------------------------------------------

/**
 * POST /api/refresh_token
 *
 * Exchanges a refresh token for new access + refresh tokens.
 * Access token TTL: 6 hours. Refresh token TTL: ~7 days.
 *
 * NOTE: Typically you don't call this directly — the apiClient handles
 * automatic refresh on 401. Exposed here for explicit refresh scenarios.
 */
export function refreshTokens(
  data: RefreshTokenRequest,
): Promise<RefreshTokenData> {
  return apiClient.post<RefreshTokenData>("/api/refresh_token", data, {
    noAuth: true,
  });
}

// ---------------------------------------------------------------------------
// 9. Current User
// ---------------------------------------------------------------------------

/**
 * GET /api/user/me
 *
 * Returns the authenticated user's profile.
 * Requires JWT.
 */
export function getCurrentUser(): Promise<UserProfile> {
  return apiClient.get<UserProfile>("/api/user/me");
}

/**
 * GET /api/uru/me
 *
 * Returns the uru-specific profile: account info, connection_code, global link URL,
 * and the full KYC scorecard (verification flags + risk level).
 * Requires JWT.
 */
export function getUruMe(): Promise<UruMeData> {
  return apiClient.get<UruMeData>("/api/uru/me");
}

/**
 * PUT /api/user/{id}
 *
 * Updates account.User fields. Protected fields (is_superuser, is_staff) are
 * blocked for non-admins. Phone number (username) is immutable.
 * Requires JWT.
 *
 * Note: POST /api/user/me (api.md) returns 404 — PUT /api/user/{id} is the
 * working endpoint for profile updates on this backend.
 */
export function updateProfile(
  userId: number,
  data: Partial<Pick<UserProfile, "first_name" | "last_name" | "email">>,
): Promise<UserProfile> {
  return apiClient.put<UserProfile>(`/api/user/${userId}`, data);
}

/**
 * Upload a new avatar image using the django-mojo 3-step initiated upload flow:
 *   1. POST /api/fileman/upload/initiate — get a file ID and upload_url
 *   2. Upload file bytes to upload_url (presigned S3 PUT or local multipart POST)
 *   3. POST /api/user/{id} — associate the file with the user profile
 *
 * Returns the resolved avatar URL string.
 * Note: mark_as_completed (POST /api/fileman/file/{id}) returns 403 for non-admins — skipped.
 * Requires JWT.
 */
export async function uploadAvatar(userId: number, file: File): Promise<string> {
  // Step 1: Initiate upload — get file record ID and upload URL
  const initiated = await apiClient.post<FileUploadInitiateData>(
    "/api/fileman/upload/initiate",
    { filename: file.name, content_type: file.type, file_size: file.size },
  );

  // Step 2: Upload file bytes to the returned upload_url
  const uploadUrl = initiated.upload_url;
  if (uploadUrl.startsWith("http")) {
    // S3 presigned PUT — send raw file bytes directly to cloud storage (no auth header)
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Storage upload failed (${response.status})`);
    }
  } else {
    // Local backend — POST as multipart FormData to /api/fileman/upload/<token>
    const form = new FormData();
    form.append("file", file);
    await apiClient.post<unknown>(uploadUrl, form);
  }

  // Step 3: Associate the file with the user's avatar field and extract the URL
  const updated = await apiClient.post<UserProfile>(`/api/user/${userId}`, {
    avatar_id: initiated.id,
  });
  const url = resolveAvatarUrl(updated.avatar);
  if (!url) throw new Error("Avatar uploaded but no URL returned from server");
  return url;
}

// ---------------------------------------------------------------------------
// 10. Connections — Resolve & CRUD
// ---------------------------------------------------------------------------

/**
 * GET /api/uru/connect/resolve/{identifier}
 *
 * Resolves a global or direct connection link. Returns the requestor's public info
 * for rendering the connection page. Detects link type by format:
 * - `uru-conn-*` → direct link (UruConnection.uuid)
 * - anything else → global link (Customer.connection_code)
 * Public — no auth required.
 */
export function resolveConnectionLink(
  code: string,
  pvToken?: string,
): Promise<ResolveConnectionData> {
  const params = pvToken ? `?pv=${encodeURIComponent(pvToken)}` : "";
  return apiClient.get<ResolveConnectionData>(`/api/uru/connect/resolve/${code}${params}`, {
    noAuth: true,
  });
}

/**
 * POST /api/uru/connect/accept/{code}
 *
 * Accept a connection via global link code or direct link UUID.
 * For global links, creates the UruConnection and accepts atomically.
 * Requires JWT.
 */
export function connectAccept(code: string): Promise<Connection> {
  return apiClient.post<Connection>(
    `/api/uru/connect/accept/${code}`,
    {},
  );
}

/**
 * POST /api/uru/connect/deny/{code}
 *
 * Silently deny a global link connection. No record created, requestor not notified.
 * For direct links, use denyConnection(pk) instead.
 * Public — no auth required.
 */
export function connectDeny(code: string): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>(
    `/api/uru/connect/deny/${code}`,
    {},
    { noAuth: true },
  );
}

/**
 * GET /api/uru/connections
 *
 * Lists connections scoped to the authenticated user.
 * Supports filtering by status, source, search, sort, and pagination.
 * Requires JWT.
 */
export function listConnections(
  params?: ConnectionsQueryParams,
): Promise<Connection[]> {
  const searchParams = new URLSearchParams();

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    }
  }

  const query = searchParams.toString();
  const url = `/api/uru/connections${query ? `?${query}` : ""}`;

  return apiClient.get<Connection[]>(url);
}

/**
 * GET /api/uru/connections/{pk}
 *
 * Returns connection detail with full graph.
 * Requires JWT.
 */
export function getConnection(pk: number): Promise<Connection> {
  return apiClient.get<Connection>(`/api/uru/connections/${pk}`);
}

/**
 * POST /api/uru/connections/create
 *
 * Creates a new single-use direct connection link with optional KYC requirements.
 * Requires JWT.
 *
 * @param config - Optional verification requirements. If omitted, backend defaults
 *   apply (gov_id + liveness + phone).
 *
 * Returns the Connection dict with `link` URL included.
 */
export function createDirectLink(
  config?: CreateDirectLinkConfig,
  recipient?: string,
): Promise<Connection> {
  const body: Record<string, unknown> = {};
  if (config) body.config = config;
  if (recipient?.trim()) body.recipient = recipient.trim();
  return apiClient.post<Connection>("/api/uru/connections/create", body);
}

// ---------------------------------------------------------------------------
// 11. Connection Actions
// ---------------------------------------------------------------------------
//
// IMPORTANT: These endpoints return the Connection dict DIRECTLY — NOT wrapped
// in the standard { status: true, data: {...} } envelope. We pass
// rawResponse: true so the apiClient skips envelope unwrapping.
// ---------------------------------------------------------------------------

/**
 * POST /api/uru/connections/{pk}
 *
 * Performs an action on a connection (accept, deny, or cancel).
 * Requires JWT.
 *
 * Returns the updated Connection dict directly (non-standard response shape).
 */
export function performConnectionAction(
  pk: number,
  action: ConnectionAction,
): Promise<Connection> {
  return apiClient.post<Connection>(
    `/api/uru/connections/${pk}`,
    action,
    { rawResponse: true },
  );
}

/**
 * Accept a pending connection.
 * Creates bidirectional link — both users appear in each other's list.
 */
export function acceptConnection(pk: number): Promise<Connection> {
  return performConnectionAction(pk, { accept: true });
}

/**
 * Deny a pending connection.
 * Global link denials are silent; direct link denials notify the requestor.
 */
export function denyConnection(
  pk: number,
  reason?: string,
): Promise<Connection> {
  return performConnectionAction(pk, { deny: { reason } });
}

/**
 * Cancel own pending connection.
 * Only the requestor can cancel — returns error if another user tries.
 */
export function cancelConnection(pk: number): Promise<Connection> {
  return performConnectionAction(pk, { cancel: true });
}

// ---------------------------------------------------------------------------
// 12. KYC Status Check
// ---------------------------------------------------------------------------

/**
 * GET /api/verify/requests/check-status/{uuid}
 *
 * Checks if KYC verification is complete. The UUID acts as a bearer token.
 * Public — no auth required.
 *
 * Use this for optional polling after registration to detect when the user
 * has completed the external KYC flow.
 */
export function checkKycStatus(uuid: string): Promise<KycStatusData> {
  return apiClient.get<KycStatusData>(
    `/api/verify/requests/check-status/${uuid}`,
    { noAuth: true },
  );
}

// ---------------------------------------------------------------------------
// 13. Notifications
// ---------------------------------------------------------------------------

/**
 * GET /api/account/notification
 *
 * Lists the authenticated user's notifications.
 * Defaults to unread only (is_unread=true).
 * Requires JWT.
 */
export function listNotifications(
  params?: NotificationsQueryParams,
): Promise<AppNotification[]> {
  const searchParams = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) searchParams.set(k, String(v));
    }
  }
  const query = searchParams.toString();
  return apiClient.get<AppNotification[]>(
    `/api/account/notification${query ? `?${query}` : ""}`,
  );
}

/**
 * POST /api/account/notification/{id}
 *
 * Mark a notification as read.
 * Requires JWT.
 */
export function markNotificationRead(id: number): Promise<void> {
  return apiClient.post<void>(`/api/account/notification/${id}`, {
    mark_read: true,
  });
}

// ---------------------------------------------------------------------------
// 14. Global Link Config
// ---------------------------------------------------------------------------

/**
 * POST /api/uru/me/config
 *
 * Save default KYC requirements and data sharing defaults for the user's global
 * connection link. These settings are applied when someone connects via the
 * permanent global URL.
 * Returns the saved config.
 */
export function saveGlobalLinkConfig(
  config: GlobalLinkConfigRequest,
): Promise<{ connection_config: Record<string, boolean> }> {
  return apiClient.post("/api/uru/me/config", config);
}

// ---------------------------------------------------------------------------
// 15. Data Sharing Actions
// ---------------------------------------------------------------------------

/**
 * POST /api/uru/connections/{pk} — sharing_request
 *
 * Request access to a specific data field from a verified connection.
 * Creates a pending entry on the other person's record and notifies them.
 * Requires JWT.
 */
export function requestDataSharing(
  pk: number,
  field: SharableField,
): Promise<Connection> {
  return performConnectionAction(pk, { sharing_request: { field } });
}

/**
 * POST /api/uru/connections/{pk} — sharing_grant
 *
 * Grant a verified connection access to one of your data fields.
 * mode: "share" = send actual value; "show_valid" = send verified flag only.
 * SSN only supports "show_valid".
 * Requires JWT.
 */
export function grantDataSharing(
  pk: number,
  field: SharableField,
  mode: SharingMode,
  expires_days?: number,
): Promise<Connection> {
  return performConnectionAction(pk, {
    sharing_grant: { field, mode, ...(expires_days !== undefined && { expires_days }) },
  });
}

/**
 * POST /api/uru/connections/{pk} — sharing_revoke
 *
 * Revoke a data sharing permission.
 * direction "outbound" = revoke what I share with them.
 * direction "inbound"  = relinquish access they granted me.
 * Requires JWT.
 */
export function revokeDataSharing(
  pk: number,
  field: SharableField,
  direction: "outbound" | "inbound",
): Promise<Connection> {
  return performConnectionAction(pk, { sharing_revoke: { field, direction } });
}

// ---------------------------------------------------------------------------
// 16. Self-Service KYC
// ---------------------------------------------------------------------------

/**
 * POST /api/uru/me/kyc/create
 *
 * Create a self-service KYC verification request for an authenticated user.
 * Data-input checks (SSN, OFAC, address, email) resolve immediately.
 * Interactive checks (gov ID, liveness) return a verification_url.
 * Requires JWT.
 *
 * Returns SelfKycCreateResponse with existing=true if a pending request already exists.
 */
export function createSelfKyc(
  request: SelfKycCreateRequest,
): Promise<SelfKycCreateResponse> {
  return apiClient.post<SelfKycCreateResponse>("/api/uru/me/kyc/create", request);
}

/**
 * GET /api/uru/me/kyc/status/{uuid}
 *
 * Poll the status of a self-service KYC verification request.
 * Only returns requests owned by the authenticated user.
 * Requires JWT.
 *
 * Poll every 3–5 seconds until pvr_status is "completed" or "failed".
 */
export function getSelfKycStatus(uuid: string): Promise<SelfKycStatusResponse> {
  return apiClient.get<SelfKycStatusResponse>(`/api/uru/me/kyc/status/${uuid}`);
}

// ---------------------------------------------------------------------------
// 17. DocSign — Public Recipient Signing (UUID bearer token, no auth)
// ---------------------------------------------------------------------------
//
// These back the public `/sign/:uuid` recipient page. The signing-request UUID
// is the bearer token — every call is { noAuth: true }. The sender side
// (templates, placements, request creation) lives in the Admin Portal.
// ---------------------------------------------------------------------------

/**
 * GET /api/docsign/sign/{uuid}/document
 *
 * Returns the (IP-bound, time-limited) PDF preview URL plus the resolved field
 * placements to overlay on the document. Throws ApiRequestError with HTTP 403
 * (code LIVENESS_REQUIRED) when the request gates signing behind a liveness
 * check that hasn't been completed yet.
 * Public — no auth required.
 */
export function getSigningDocument(uuid: string): Promise<DocSignDocumentData> {
  return apiClient.get<DocSignDocumentData>(
    `/api/docsign/sign/${uuid}/document`,
    { noAuth: true },
  );
}

/**
 * GET /api/docsign/sign/{uuid}/status
 *
 * Lightweight status poll for a signing request (never gated by liveness).
 * Public — no auth required.
 */
export function getSigningStatus(uuid: string): Promise<DocSignStatusData> {
  return apiClient.get<DocSignStatusData>(
    `/api/docsign/sign/${uuid}/status`,
    { noAuth: true },
  );
}

/**
 * GET /api/docsign/sign/{uuid}/existing-signatures
 *
 * Returns up to 5 of the signer's reusable signatures on file (if any).
 * Public — no auth required.
 */
export function getExistingSignatures(
  uuid: string,
): Promise<ExistingSignaturesData> {
  return apiClient.get<ExistingSignaturesData>(
    `/api/docsign/sign/${uuid}/existing-signatures`,
    { noAuth: true },
  );
}

/**
 * POST /api/docsign/sign/{uuid}/submit
 *
 * Submits the captured signature (base64 PNG + stroke points) and generates the
 * signed PDF. Returns the completion record on success.
 * Public — no auth required.
 */
export function submitSignature(
  uuid: string,
  payload: SubmitSignatureRequest,
): Promise<SubmitSignatureData> {
  return apiClient.post<SubmitSignatureData>(
    `/api/docsign/sign/${uuid}/submit`,
    payload,
    { noAuth: true },
  );
}

// ---------------------------------------------------------------------------
// 18. DocSign — Admin read models (signed-in "Documents" page)
// ---------------------------------------------------------------------------
//
// The logged-in uru user is also the MojoVerify portal admin (same account.User),
// so these existing admin endpoints work with the user's JWT — no uru wrapper
// needed to *list* what they created. Requires JWT.
// ---------------------------------------------------------------------------

/**
 * GET /api/docsign/request
 *
 * List the signing requests visible to the authenticated admin (scoped to their
 * group). Each row's `uuid` is the bearer token for the shareable signing link.
 */
export function listSigningRequests(
  params?: Record<string, string | number>,
): Promise<DocSignRequestSummary[]> {
  const search = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) search.set(k, String(v));
  }
  const q = search.toString();
  return apiClient.get<DocSignRequestSummary[]>(
    `/api/docsign/request${q ? `?${q}` : ""}`,
  );
}

/**
 * GET /api/docsign/template
 *
 * List the group's document templates. Requires JWT.
 */
export function listDocTemplates(): Promise<DocumentTemplateSummary[]> {
  return apiClient.get<DocumentTemplateSummary[]>("/api/docsign/template");
}

/**
 * GET /api/group
 *
 * List the groups the authenticated user belongs to. Used to discover the
 * admin/org group(s) to scope docsign queries to (the user's personal uru
 * group has no signing requests). Requires JWT.
 */
export function listGroups(): Promise<AccountGroup[]> {
  return apiClient.get<AccountGroup[]>("/api/group");
}

/**
 * POST /api/shortlink/link/create
 *
 * Mint a short link for any URL using the shared shortlink service (the same
 * one uru SMS/KYC links use). Only needs an authenticated user — no special
 * perm — so the uru JWT works directly; no docsign-specific backend needed.
 * Returns `{ short_link, original_url }`. `base_url` selects the short domain
 * (defaults to the uru short domain).
 */
const SHORTLINK_BASE =
  (import.meta.env as unknown as Record<string, string | undefined>)
    .VITE_SHORTLINK_BASE_URL || "https://youru.io";

export function createSigningShortlink(
  url: string,
): Promise<{ short_link: string; original_url: string }> {
  return apiClient.post<{ short_link: string; original_url: string }>(
    "/api/shortlink/link/create",
    {
      url,
      source: "uru_docsign",
      base_url: SHORTLINK_BASE,
      metadata: {
        "og:title": "Sign your document",
        "og:description": "Securely review and sign — YouAreYou",
      },
    },
  );
}
